import { FC, Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    Grid,
    DialogActions,
    DialogContent,
    TextField,
    CircularProgress,
    Button,
    FormControlLabel,
    FormLabel,
    FormGroup,
    FormHelperText,
    Box
} from "@mui/material";
import { useSnackbar } from "notistack";
import Checkbox from '@mui/material/Checkbox';
import http from "src/utils/httpHelper";
import Text from "src/components/Text";
import Role from "src/models/role";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";

interface RolesFormProps {
    modalCloseCallback,
    onSuccessCallback,
    item?: Role
}

const checkboxArrayHandler = (fieldValue, e) => {
    let checkedBoxes = fieldValue;
    const checkValue = e.target.value;
    if (e.target.checked) {
        checkedBoxes.push(checkValue);
    } else {
        checkedBoxes = checkedBoxes.filter(checkedBox => checkedBox !== checkValue);
    }
    return checkedBoxes;
}

const RolesForm: FC<RolesFormProps> = ({ modalCloseCallback, onSuccessCallback, item }) => {
    const { t }: { t: any } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const [permissions, setPermissions] = useState([]);

    useEffect(() => {
        http.get('api/permissions/for-select')
            .then((response) => setPermissions(response.data))
            .catch(error => {
                enqueueSnackbar(error.message, { variant: 'error' });
            });
    }, [enqueueSnackbar]);

    const { handleSubmit, formState: { errors, isSubmitting, isValid }, control, reset } = useForm({
        mode: 'onTouched',
        resolver: yupResolver(Role.getRules()),
        defaultValues: Role.getInitValues(item)
    });


    return (
        <form noValidate onSubmit={handleSubmit(async (values) => {
            await http.request({
                method: item ? 'put' : 'post',
                url: item ? `/api/roles/${item.id}` : '/api/roles',
                data: values
            })
                .then((response) => {
                    onSuccessCallback();
                    enqueueSnackbar(response.data.message, { variant: 'success' });
                })
                .catch((error) => {
                    console.error(error);
                    enqueueSnackbar(error.message, { variant: 'error' });
                });
        })}>
            <DialogContent
                sx={{
                    p: 3,
                }}
            >
                <Grid container spacing={3}>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="name"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    error={Boolean(errors.name)}
                                    fullWidth
                                    helperText={errors.name?.message}
                                    label={t("FIELDS.NAME")}
                                    name="name"
                                    variant="outlined"
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Controller
                            name="description"
                            control={control}
                            render={({ field }) => (
                                <TextField
                                    error={Boolean(errors.description)}
                                    fullWidth
                                    helperText={errors.description?.message}
                                    label={t("FIELDS.DESCRIPTION")}
                                    name="description"
                                    variant="outlined"
                                    {...field}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} md={12}>
                        <Text color="primary">{t('ROLES.PERMISSIONS')}</Text>
                    </Grid>

                    <Grid item xs={12} md={12}>
                        <Grid container>
                            <Controller
                                name={`permissions`}
                                control={control}
                                render={({ field, field: { onChange, value } }) => (
                                    <Fragment>
                                        {
                                            permissions.map((permissionGroup) => (
                                                <Grid item xs={12} md={3} key={permissionGroup.id}>
                                                    <FormLabel component="legend">{permissionGroup.name}</FormLabel>
                                                    <FormGroup>
                                                        {
                                                            permissionGroup.permissions.map((permission) => (
                                                                <FormControlLabel key={permission.id}
                                                                    control={
                                                                        <Checkbox
                                                                            {...field}
                                                                            size="small"
                                                                            value={permission.id}
                                                                            checked={value.includes(permission.id + '')}
                                                                            onChange={(e) => onChange(checkboxArrayHandler(value, e))}
                                                                        />
                                                                    }
                                                                    label={permission.name}
                                                                />
                                                            ))
                                                        }
                                                    </FormGroup>
                                                </Grid>
                                            ))
                                        }
                                    </Fragment>
                                )}
                            />
                            {
                                Boolean(errors.permissions) &&
                                <FormHelperText error={true}>{errors.permissions?.message}</FormHelperText>
                            }
                        </Grid>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions
                sx={{
                    p: 3,
                    display: 'flex',
                    justifyContent: 'flex-end'
                }}
            >
                <Box>

                    <Button color="secondary" onClick={modalCloseCallback}>
                        {t("GENERAL.CANCEL")}
                    </Button>
                    <Button
                        sx={{
                            ml: 2
                        }}
                        type="submit"
                        startIcon={
                            isSubmitting ? <CircularProgress size="1rem" /> : null
                        }
                        disabled={isSubmitting || !isValid}
                        variant="contained"
                    >
                        {t(!item ? "ROLES.CREATE" : 'ROLES.EDIT')}
                    </Button>
                </Box>
            </DialogActions>
        </form>
    )
}

export default RolesForm;